import React, { useState } from 'react';
import Image, { IImage } from 'tbk-components/src/components/Image';
import {
	DashboardCardStatus,
	DashboardCardTags,
	IDashboardCareerCardProps,
} from './DashboardCard';
import Modal from 'tbk-components/src/components/Modal';
import logo from '../../../assets/images/logo-colour.svg';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Icon from 'tbk-components/src/components/Icon';
import DashboardApplicationModal from './DashboardApplicationModal';

export interface IDashboardCardModalI18n {
	didYouCompleteYourApplicationHeading: string;
	didYouCompleteYourApplicationDescription: string;
	didYouCompleteYourApplicationConfirmation: string;
	careerManagedThroughHeading: string;
	careerManagedThroughDescription: string;
	careerManagedThroughCta: string;
	viewJob: string;
	completeApplication: string;
	oneStepApply: string;
	fullApplicationSubmitted: string;
	quickApplicationSubmitted: string;
	notificationFormSubmitted: string;
	logo: IImage;
	allCareersLink: ILink;
}

export interface IDashboardCardModalProps {
	i18n?: IDashboardCardModalI18n;
	onCancel?: () => void;
	onCompletedApplication?: (ID: number) => void;
	card?: IDashboardCareerCardProps;
}

const DashboardCardModal: React.FC<IDashboardCardModalProps> = ({
	i18n = {
		didYouCompleteYourApplicationHeading:
			'Did you complete your application?',
		didYouCompleteYourApplicationDescription:
			'If you fully applied to this role by submitting your resume and cover letter <strong>then mark this complete</strong>. Marking this complete will remove this alert on your dashboard.',
		didYouCompleteYourApplicationConfirmation:
			'Yes, I completed my application',
		careerManagedThroughHeading: 'This career is managed through %s',
		careerManagedThroughDescription:
			'Here at Calian we use many career management websites specific for the industries we serve. This career is managed through %s.',
		careerManagedThroughCta: 'Open %s',
		fullApplicationSubmitted: 'Full application submitted',
		quickApplicationSubmitted: 'Application started but not completed',
		notificationFormSubmitted: 'Notification form submitted',
		viewJob: 'View job',
		completeApplication: 'Complete application',
		oneStepApply: '1-step apply',
		logo: {
			src: logo,
		} as IImage,
		allCareersLink: {
			href: '#',
			label: 'View all careers',
		} as ILink,
	} as IDashboardCardModalI18n,
	onCancel = () => void 0,
	onCompletedApplication,
	card,
}) => {
	const isClosed = card.careerTags?.find(
		(tag) => tag.taxonomy === 'status' && tag.slug === 'closed',
	)
		? true
		: false;
	const needsAttention =
		!card.fullApplicationSubmitted && !card.hasBeenDismissed && !isClosed;
	const [showApplicationModal, setShowApplicationModal] = useState(false);

	return (
		<>
			<Modal
				onCancel={onCancel}
				headerClassNames={['border-0 p-4 p-lg-5']}
				modalClassNames={['modal-dialog-centered modal-xl']}
				bodyClassNames={['modal-body px-3 py-0 px-lg-5 px-xl-9']}
				id="dashboard-card-modal"
				trapFocus
			>
				<div className="container border-1 px-2 px-lg-4 pb-5 pb-xl-9">
					<div className="row px-1 px-lg-3 mb-3 mb-lg-6">
						<div className="col-12 col-lg-10 px-0">
							<h3 className="h2 mb-2">{card.careerTitle}</h3>
							{card.careerTags.length && (
								<DashboardCardTags
									tags={card.careerTags.slice(0, 1)}
									classNames={['d-inline']}
								/>
							)}
							{!isClosed && (
								<span className="ms-2 fs-8">
									<DashboardCardStatus
										card={card}
										needsAttention={needsAttention}
										i18n={i18n}
									/>
								</span>
							)}
						</div>
						<div className="col-12 col-lg-2 text-lg-end mt-3 mt-lg-0 px-0 pt-3 border-top border-bottom border-light-border border-lg-0">
							{card.careerTags.length && (
								<DashboardCardTags
									tags={card.careerTags.slice(1)}
								/>
							)}
							<ul className="list-inline list-group-horizontal mt-2 fs-8">
								<li className="d-inline ms-2">
									{(card.careerLocation || []).map(
										(location, index) => (
											<span
												key={index}
												className="calian-career-card__location"
											>
												{location.name}
												{location.province
													? ` , ${location.province}`
													: ''}
											</span>
										),
									)}
								</li>
								{card.careerType && (
									<li className="d-inline ms-2">
										{card.careerType}
									</li>
								)}
								{card.careerOfficeType && (
									<li className="d-inline ms-2">
										{card.careerOfficeType}
									</li>
								)}
							</ul>
						</div>
					</div>
					{(needsAttention && !card.isDeskflow && onCompletedApplication ) && (
						<div className="row px-0 mb-3 mb-lg-6">
							<div className="card border-1 rounded-4 px-3 py-3 px-lg-5 py-lg-4 border-career-dashboard-card-yellow-dark bg-career-dashboard-card-yellow-light">
								<h4 className="h6 mb-3">
									{i18n.didYouCompleteYourApplicationHeading}
								</h4>
								<p
									className="fs-8 fs-lg-7 mb-3"
									dangerouslySetInnerHTML={{
										__html: i18n.didYouCompleteYourApplicationDescription,
									}}
								/>
								<label
									htmlFor={`attention-modal-${card.ID}-checkbox`}
									className="attention-modal-checkbox d-flex align-items-center"
								>
									<input
										className="form-check-input position-absolute"
										type="checkbox"
										id={`attention-modal-${card.ID}-checkbox`}
										onChange={(e) => {
											e.target.checked &&
												onCompletedApplication(card.ID);
										}}
									/>
									<span className="ms-6 fs-8 fs-lg-7">
										{
											i18n.didYouCompleteYourApplicationConfirmation
										}
									</span>
								</label>
							</div>
						</div>
					)}
					<div className="row mx-1 mt-4 align-items-center pb-4 border-bottom">
						<div className="col-12 col-lg-9 ps-0">
							<h4 className="h6 text-mid-grey mb-3">
								{i18n.careerManagedThroughHeading.replace(
									'%s',
									card.sourceATS.label,
								)}
							</h4>
							<p className="fs-8 fs-lg-7 mb-2 text-mid-grey">
								{i18n.careerManagedThroughDescription.replace(
									'%s',
									card.sourceATS.label,
								)}
							</p>
							{(!isClosed && !card.isDeskflow) && (
								<Button
									href={card.sourceATS.link}
									size="sm"
									className="btn btn-link text-decoration-none bg-transparent border-0 p-0 fs-8 fs-lg-7"
									target="_blank"
									rel="noopener noreferrer"
									label={i18n.careerManagedThroughCta.replace(
										'%s',
										card.sourceATS.label,
									)}
								>
									<span className="ms-2">
										<i className="icon icon-new-window"></i>
									</span>
								</Button>
							)}
						</div>
						{i18n.logo && (
							<div className="d-none d-lg-block col-lg-3 text-sm-end pe-0">
								<Image {...i18n.logo} width={140} />
							</div>
						)}
					</div>
					<div className="container-fluid">
						<div className="row px-0 mt-2 mt-md-4 modal-buttons text-center text-lg-start">
							{isClosed ? (
								<div className="col ps-0">
									<Button
										{...i18n.allCareersLink}
										className="btn text-primary mx-1 px-0 bg-transparent border-0 text-decoration-none"
									>
										<span className="ms-2">
											<Icon name="arrow-right" />
										</span>
									</Button>
								</div>
							) : (
								<div className="d-flex justify-content-center justify-content-md-start px-0">
									{needsAttention &&
										(card.isDeskflow ? (
											<Button
												label={i18n.oneStepApply}
												onClick={() =>
													setShowApplicationModal(
														true,
													)
												}
												className="btn btn-primary me-4"
											/>
										) : (
											<Button
												href={card.sourceATS.link}
												target="_blank"
												rel="noopener noreferrer"
												label={i18n.completeApplication}
												className="btn btn-primary me-4"
											/>
										))}
									<Button
										href={card.careerLink.href}
										className="btn flex-shrink-0 text-primary px-0 bg-transparent border-0 text-decoration-none mx-1"
										label={i18n.viewJob}
									>
										<span className="ms-2">
											<Icon name="arrow-right" />
										</span>
									</Button>
								</div>
							)}
						</div>
					</div>
				</div>
			</Modal>
			{showApplicationModal && (
				<DashboardApplicationModal
					careerID={card.ID}
					onCancel={() => setShowApplicationModal(false)}
				/>
			)}
		</>
	);
};

export default DashboardCardModal;
